// Pure builders for GET /connections, GET /home and the activity counts. The
// API router and the front end's mock server (web/vite.config.ts) both call
// these, so mock mode serves exactly the shapes production does.

import type {
  AccountKind,
  AccountView,
  ActivityEntry,
  ActivitySummary,
  AttentionLogin,
  Connections,
  ConnectionTotals,
  HomeReviewItem,
  HomeSummary,
  LastSync,
  LoginView,
  PlaidConnection,
  ReviewItem,
  ServiceConnection,
  UnmatchedItem,
} from './api-types.ts';
import { inferDueDay, suggestClosingDay, type CardLine } from '../../../src/match/statements.ts';
import { statementSettingsView, type StatementSetting } from './statements.ts';

const DAY_MS = 86_400_000;

/** The fields of a stored bank account these builders read (StoredAccount in src/state/types.ts fits). */
export interface AccountRecord {
  accountId: string;
  itemId: string;
  mask: string | null;
  name: string;
  /** "type/subtype", e.g. "credit/credit card". */
  type: string;
  /** 'not_shared': deselected by the owner in Plaid. Absent means active. */
  status?: 'active' | 'not_shared';
  notSharedAt?: string | null;
}

// ---------- per-account stats (written by the sync, read by the API) ----------

export interface AccountStats {
  accountId: string;
  /** Posted lines since the fiscal year start. */
  lines: number;
  lastLineDate: string | null;
  /** Most common Plaid account_owner on the account's lines. */
  cardholder: string | null;
  updatedAt: string;
  /** Cards: the payment due day the card's payments point to (src/match/statements.ts). */
  inferredDueDay?: number | null;
  /** Cards: a closing day suggested from autopay amounts, when they equal each statement's total. */
  suggestedClosingDay?: number | null;
}

/**
 * Per-account counts, and for card accounts the due day and closing day the
 * lines point to. Pass the fiscal year's posted lines.
 */
export function computeAccountStats(
  lines: { accountId: string; date: string; cardholder?: string | null; amount?: number; description?: string }[],
  updatedAt: string,
  cardAccountIds: Set<string> = new Set(),
): AccountStats[] {
  const by = new Map<string, { lines: number; last: string | null; owners: Map<string, number>; card: CardLine[] }>();
  for (const l of lines) {
    const s = by.get(l.accountId) ?? { lines: 0, last: null, owners: new Map<string, number>(), card: [] };
    s.lines++;
    if (!s.last || l.date > s.last) s.last = l.date;
    const owner = l.cardholder?.trim();
    if (owner) s.owners.set(owner, (s.owners.get(owner) ?? 0) + 1);
    if (cardAccountIds.has(l.accountId) && typeof l.amount === 'number') s.card.push({ date: l.date, amount: l.amount, description: l.description ?? '' });
    by.set(l.accountId, s);
  }
  return [...by].map(([accountId, s]) => ({
    accountId,
    lines: s.lines,
    lastLineDate: s.last,
    cardholder: [...s.owners].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? null,
    updatedAt,
    ...(cardAccountIds.has(accountId) ? { inferredDueDay: inferDueDay(s.card)?.day ?? null, suggestedClosingDay: suggestClosingDay(s.card)?.day ?? null } : {}),
  }));
}

// ---------- Plaid login status ----------

/** Plaid error codes the account owner fixes by signing in again on Plaid's page (update mode). */
export const SIGN_IN_AGAIN_CODES = [
  'ITEM_LOGIN_REQUIRED',
  'PENDING_EXPIRATION',
  'PENDING_DISCONNECT',
  'ITEM_LOCKED',
  'INVALID_CREDENTIALS',
  'INVALID_MFA',
  'INVALID_UPDATED_USERNAME',
  'USER_PERMISSION_REVOKED',
] as const;

export function isSignInCode(code: string | null | undefined): boolean {
  return Boolean(code) && (SIGN_IN_AGAIN_CODES as readonly string[]).includes(code!);
}

/** True for login_required, and for older 'error' records whose message carries one of the codes. */
export function needsSignIn(c: Pick<PlaidConnection, 'status' | 'error'>): boolean {
  if (c.status === 'login_required') return true;
  if (c.status !== 'error' || !c.error) return false;
  return SIGN_IN_AGAIN_CODES.some((code) => c.error!.includes(code));
}

// ---------- fiscal year and trip dates ----------

/** The fiscal year's last day: a year after its start, less a day. */
export function fiscalYearEnd(fiscalYearStart: string): string {
  const d = new Date(`${fiscalYearStart}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear() + 1, d.getUTCMonth(), d.getUTCDate()) - DAY_MS).toISOString().slice(0, 10);
}

/** The trip-date range the sync reads payments for (TRIP_DATE_FROM/TO). None without a start; the end defaults to the fiscal year's end. */
export function tripDateRange(fiscalYearStart: string, from: string | undefined, to: string | undefined): { from: string; to: string } | undefined {
  return from ? { from, to: to ?? fiscalYearEnd(fiscalYearStart) } : undefined;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Dec 1 to Dec 31, 2025", or "Dec 1, 2025 to Nov 30, 2026" across years. */
export function dateRangeText(from: string, to: string): string {
  const part = (d: string) => `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;
  return from.slice(0, 4) === to.slice(0, 4) ? `${part(from)} to ${part(to)}, ${to.slice(0, 4)}` : `${part(from)}, ${from.slice(0, 4)} to ${part(to)}, ${to.slice(0, 4)}`;
}

// ---------- GET /connections ----------

/** Plaid's "type/subtype" (or "wise/") as a display kind. */
export function displayKind(type: string): AccountKind {
  const [t, sub = ''] = type.toLowerCase().split('/');
  if (t === 'credit') return 'card';
  if (t === 'wise') return 'balance';
  if (t === 'depository') {
    if (sub.includes('checking')) return 'checking';
    if (sub.includes('saving') || sub.includes('money market') || sub === 'cd') return 'savings';
  }
  return 'other';
}

export interface ConnectionInputs {
  plaid: PlaidConnection[];
  accounts: AccountRecord[];
  stats: AccountStats[];
  services: ServiceConnection[];
  lastSync: LastSync | null;
  fiscalYearStart: string;
  /** The sync's trip-date range, when it selects payments by trip date. */
  tripDates?: { from: string; to: string };
  /** Last four of the primary checking account (PRIMARY_CHECKING_MASK), if configured. */
  primaryCheckingMask?: string;
  /** Dev only: disconnected logins' test data may be deleted. */
  purgeAllowed?: boolean;
  /** Admins' statement closing and due days per card. */
  statementSettings?: StatementSetting[];
}

const WISE_ITEM = 'wise';

export function buildConnections(i: ConnectionInputs): Connections {
  const stats = new Map(i.stats.map((s) => [s.accountId, s]));
  const settings = new Map((i.statementSettings ?? []).map((s) => [s.accountId, s]));
  const view = (a: AccountRecord): AccountView => {
    const s = stats.get(a.accountId);
    const kind = a.itemId === WISE_ITEM ? 'balance' : displayKind(a.type);
    return {
      accountId: a.accountId,
      name: a.name,
      mask: a.mask || null,
      kind,
      cardholder: s?.cardholder ?? null,
      linesThisYear: s ? s.lines : null,
      lastLineDate: s?.lastLineDate ?? null,
      ...(a.status === 'not_shared' ? { notSharedSince: a.notSharedAt ?? null } : {}),
      ...(kind === 'checking' && i.primaryCheckingMask && a.mask === i.primaryCheckingMask ? { xeroFeed: true } : {}),
      ...(kind === 'card' ? { statement: statementSettingsView(settings.get(a.accountId), s) } : {}),
    };
  };
  const byItem = new Map<string, AccountView[]>();
  for (const a of i.accounts) byItem.set(a.itemId, [...(byItem.get(a.itemId) ?? []), view(a)]);
  const sortAccounts = (list: AccountView[]) => list.sort((a, b) => (b.linesThisYear ?? -1) - (a.linesThisYear ?? -1) || a.name.localeCompare(b.name));

  const logins: LoginView[] = [...i.plaid]
    .sort((a, b) => a.institution.localeCompare(b.institution) || a.linkedAt.localeCompare(b.linkedAt))
    .map((p) => ({
      id: p.itemId,
      source: 'plaid' as const,
      institution: p.institution,
      status: p.status,
      needsSignIn: needsSignIn(p),
      lastSync: p.lastSync,
      linkedAt: p.linkedAt,
      ...(p.error ? { error: p.error } : {}),
      ...(p.disconnectedAt ? { disconnectedAt: p.disconnectedAt } : {}),
      ...(p.disconnectedBy ? { disconnectedBy: p.disconnectedBy } : {}),
      ...(p.newAccountsAt && p.status !== 'disconnected' ? { newAccountsAt: p.newAccountsAt } : {}),
      accounts: sortAccounts(byItem.get(p.itemId) ?? []),
    }));

  // Accounts whose login record is gone (shouldn't happen) still show, so nothing is hidden.
  const known = new Set([...i.plaid.map((p) => p.itemId), WISE_ITEM]);
  for (const [itemId, accounts] of byItem) {
    if (known.has(itemId)) continue;
    logins.push({ id: itemId, source: 'plaid', institution: 'Unknown login', status: 'error', needsSignIn: false, lastSync: null, linkedAt: null, error: 'No login record for these accounts', accounts: sortAccounts(accounts) });
  }

  const wise = i.services.find((s) => s.name === 'wise');
  const wiseAccounts = byItem.get(WISE_ITEM) ?? [];
  if (wiseAccounts.length || (wise && wise.status !== 'not_configured')) {
    logins.push({
      id: WISE_ITEM,
      source: 'wise',
      institution: 'Wise',
      status: wise?.status ?? 'pending',
      needsSignIn: false,
      lastSync: wise?.status === 'ok' ? wise.checkedAt : null,
      linkedAt: null,
      ...(wise && wise.status !== 'ok' ? { error: wise.detail } : {}),
      note: 'API token, read-only',
      accounts: sortAccounts(wiseAccounts),
    });
  }

  // The primary checking account carries checks and wires. If no connected account has its mask, say so.
  const mask = i.primaryCheckingMask;
  if (mask && !logins.some((l) => l.accounts.some((a) => a.xeroFeed))) {
    logins.push({
      id: 'primary-checking',
      source: 'config',
      institution: 'Primary checking',
      status: 'not_configured',
      needsSignIn: false,
      lastSync: null,
      linkedAt: null,
      note: 'Checks and wires clear here. Connect it through Plaid so they can be matched.',
      accounts: [{ accountId: `config-${mask}`, name: 'Checking', mask, kind: 'checking', cardholder: null, linesThisYear: null, lastLineDate: null, xeroFeed: true }],
    });
  }

  return {
    plaid: i.plaid, logins, totals: connectionTotals(logins), services: i.services, lastSync: i.lastSync, fiscalYearStart: i.fiscalYearStart,
    ...(i.tripDates ? { tripDates: i.tripDates } : {}),
    purgeAllowed: i.purgeAllowed ?? false,
  };
}

/** A login that's been removed (status not_configured on Plaid) or disconnected no longer counts anywhere. */
export const isRemoved = (l: LoginView) => l.source !== 'config' && (l.status === 'not_configured' || l.status === 'disconnected');
export const isDisconnected = (l: Pick<LoginView, 'status'>) => l.status === 'disconnected';
/** An account the owner stopped sharing (manage accounts). Kept for the record; not counted. */
export const isNotShared = (a: Pick<AccountView, 'notSharedSince'>) => a.notSharedSince !== undefined;
/** Sign-in needed or failing: lines have stopped. */
export const isFailing = (l: LoginView) => !isRemoved(l) && l.source !== 'config' && (l.needsSignIn || l.status === 'error');
/** The bank has an account the owner hasn't shared yet (not a failure; the rest keeps syncing). */
export const hasNewAccounts = (l: Pick<LoginView, 'source' | 'status' | 'newAccountsAt'>) =>
  l.source === 'plaid' && l.status !== 'disconnected' && l.status !== 'not_configured' && Boolean(l.newAccountsAt);
export const needsAttention = (l: LoginView) => isFailing(l) || hasNewAccounts(l);

export function connectionTotals(logins: LoginView[]): ConnectionTotals {
  const t: ConnectionTotals = { accounts: 0, syncing: 0, attention: 0, notConnected: 0, attentionLogins: 0, linesThisYear: 0 };
  for (const l of logins) {
    if (isRemoved(l)) continue;
    const shared = l.accounts.filter((a) => !isNotShared(a));
    const n = shared.length;
    t.accounts += n;
    if (l.source === 'config') t.notConnected += n;
    else if (isFailing(l)) {
      t.attention += n;
      t.attentionLogins++;
    } else {
      if (l.status === 'ok') t.syncing += n;
      // A new account waiting to be shared: one account to add, while the login's others keep syncing.
      if (hasNewAccounts(l)) {
        t.attention += 1;
        t.attentionLogins++;
      }
    }
    for (const a of shared) t.linesThisYear += a.linesThisYear ?? 0;
  }
  return t;
}

// ---------- GET /home ----------

export interface HomeInputs {
  now: Date;
  pending: ReviewItem[];
  unmatched: { asOf: string | null; items: UnmatchedItem[] };
  activity: ActivityEntry[];
  connections: Connections;
  autoMatchedThisMonth: number;
}

const isoDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const paymentsTotal = (m: ReviewItem) => m.payments.reduce((sum, p) => sum + p.amount, 0);

/** Pending items as decisions: held-check alternatives (same group) count once, newest first. */
export function reviewGroups(pending: ReviewItem[]): ReviewItem[][] {
  const groups = new Map<string, ReviewItem[]>();
  const sorted = [...pending].sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
  for (const m of sorted) {
    const k = m.group ?? m.id;
    groups.set(k, [...(groups.get(k) ?? []), m]);
  }
  return [...groups.values()];
}

export function buildHome(i: HomeInputs): HomeSummary {
  const groups = reviewGroups(i.pending);
  const top: HomeReviewItem[] = groups.slice(0, 3).map((g) => {
    const m = g[0];
    return {
      id: m.id,
      rule: m.rule,
      title: m.payments[0]?.vendor || m.lines[0]?.description || m.account,
      reason: m.reason,
      trip: m.payments[0]?.trip ?? '',
      account: m.account,
      amount: paymentsTotal(m),
      alternatives: g.length,
    };
  });

  const cutoff = isoDate(i.now.getTime() - 30 * DAY_MS);
  const lines = i.unmatched.items.filter((u) => u.kind === 'line');
  const payments = i.unmatched.items.filter((u) => u.kind === 'payment');
  const old = (u: UnmatchedItem) => u.date < cutoff;

  const c = i.connections;
  const attention: AttentionLogin[] = c.logins.filter(needsAttention).map((l) => ({
    id: l.id,
    institution: l.institution,
    accounts: l.accounts.map((a) => ({ name: a.name, mask: a.mask })),
    needsSignIn: l.needsSignIn,
    since: l.lastSync,
    ...(l.error ? { error: l.error } : {}),
    ...(hasNewAccounts(l) ? { newAccountsAt: l.newAccountsAt } : {}),
  }));

  return {
    generatedAt: i.now.toISOString(),
    lastSync: c.lastSync,
    review: { count: groups.length, total: groups.reduce((sum, g) => sum + Math.abs(paymentsTotal(g[0])), 0), top },
    unmatched: {
      asOf: i.unmatched.asOf,
      lines: { count: lines.length, over30: lines.filter(old).length },
      payments: { count: payments.length, over30: payments.filter(old).length, checksOver30: payments.filter((p) => old(p) && /check/i.test(p.method ?? '')).length },
    },
    autoMatchedThisMonth: i.autoMatchedThisMonth,
    activity: i.activity,
    connections: {
      totals: c.totals,
      attention,
      notConnected: c.logins.filter((l) => l.source === 'config').map((l) => ({ id: l.id, institution: l.institution })),
      services: c.services,
    },
  };
}

/** First instant of the calendar month (UTC), as an ISO string. */
export function monthStart(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

/** Matches the rules confirmed with no person involved. */
export function isAutoMatch(m: Pick<ReviewItem, 'status' | 'decidedBy'>): boolean {
  return (m.status === 'confirmed' || m.status === 'posted') && !m.decidedBy;
}

// ---------- activity counts ----------

export const ACTIVITY_WINDOW_DAYS = 7;

export function activitySince(now: Date): string {
  return new Date(now.getTime() - ACTIVITY_WINDOW_DAYS * DAY_MS).toISOString();
}

export function summarizeActivity(entries: Pick<ActivityEntry, 'at' | 'action'>[], since: string): ActivitySummary {
  const s: ActivitySummary = { since, posted: 0, approved: 0, rejected: 0 };
  for (const e of entries) {
    if (e.at < since) continue;
    if (e.action === 'posted') s.posted++;
    else if (e.action === 'approved') s.approved++;
    else if (e.action === 'rejected') s.rejected++;
  }
  return s;
}
